// TODO CRPM-4245 27R1: remove the lookup, and this file with it, together with the legacy Insights
// links; see "Legacy Insights links" in TODO.md.
import { useState } from 'react'
import { redirect, useRouterState } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Alert } from '@mui/material'
import { DashboardsApi } from '@portal/pkg-api/dashboard'
import { data } from '@portal/pkg-api/shared'
import { isNotFound } from '@dashboard/data'

declare module '@tanstack/react-router' {
  interface HistoryState {
    /** Set on the dashboard page when it was reached by redirect from an old Insights link. */
    legacyInsightsLink?: boolean
  }
}

/** The `/404` reason for an old Insights link that leads nowhere. */
export const LEGACY_INSIGHTS_LINK_REASON = 'legacyInsightsLink'

// At most 15 digits, so the id is exact as a JavaScript number.
const LEGACY_ID = /^[0-9]{1,15}$/

const findDashboardId = async (legacyId: number) => {
  try {
    const { id } = await data(
      DashboardsApi.getInsightsDashboardByLegacyId(legacyId, {
        ignore404: true,
        mercatusNotifyOnError: false,
      }),
    )
    return id
  } catch (error) {
    if (isNotFound(error)) {
      return undefined
    }
    throw error
  }
}

/**
 * Redirects an old `/insights/dashboards?dashboard=<id>` or `/insights/reports?report=<id>` link,
 * whose id is the dashboard's legacy `insight_dashboard` id, to `/insights/<dashboard id>`. Any
 * other query parameters, and the `#` fragment (`hash`, without its `#`), are kept, as V1182 keeps
 * them in menu links. A link that leads nowhere goes to the old-link 404 page.
 */
export const redirectLegacyInsightsLink = async (searchStr: string, hash = ''): Promise<never> => {
  const params = new URLSearchParams(searchStr)
  // The old pages used a non-empty `dashboard`, and otherwise `report`, whichever path they were on.
  const legacyId = params.get('dashboard') || params.get('report') || ''
  params.delete('dashboard')
  params.delete('report')

  const dashboardId = LEGACY_ID.test(legacyId) ? await findDashboardId(Number(legacyId)) : undefined
  if (dashboardId === undefined) {
    throw redirect({ to: '/404', search: { reason: LEGACY_INSIGHTS_LINK_REASON }, replace: true })
  }

  const otherParams = params.toString()
  throw redirect({
    href: `/insights/${dashboardId}${otherParams ? `?${otherParams}` : ''}${hash ? `#${hash}` : ''}`,
    replace: true,
    state: { legacyInsightsLink: true },
  })
}

export const useIsFromLegacyInsightsLink = () =>
  useRouterState({ select: ({ location }) => !!location.state.legacyInsightsLink })

/** Asks the user to replace the old link they arrived by. */
export const LegacyInsightsLinkNotice = () => {
  const { t } = useTranslation('dashboard')
  const [open, setOpen] = useState(true)

  if (!open) {
    return null
  }
  return (
    <Alert severity="warning" onClose={() => setOpen(false)}>
      {t('insights.legacyLink')}
    </Alert>
  )
}
